import { slugify } from '@superbet-group/web.lib.utilities';

import {
    ActionContext,
    GetterTree,
} from 'vuex';

import { RouteName } from '@app/router/constants/RouteName';
import { IState } from '@app/store';
import StoreUtil from '@store/StoreUtil';

import {
    getQueryParamsAfterTabChange,
    getCompetitionRouteParams,
} from './helpers';
import {
    AvailableCompetitionTabs,
    CompetitionsState,
    FeatureType,
} from './types';
import { simplifyTournamentName } from '@core/utils/sportsOffer/simplifyTournamentName';
import { VirtualTournament } from '@sportOfferFederation/competitions/types';

const mutations = {
    SET_SELECTED_TAB: 'SET_SELECTED_TAB',
};

function defaultState(): CompetitionsState {
    return {
        selectedTab: AvailableCompetitionTabs.Matches,
    };
}

type GetterWithAllArguments<T> = (
    state: CompetitionsState,
    getters: any,
    rootState: IState,
    rootGetters: any,
) => T;
interface StoreGetters {
    availableTabs: GetterWithAllArguments<CompetitionTab[]>;
    isOnCompetitionPage: GetterWithAllArguments<boolean>;
    currentSportId: GetterWithAllArguments<number | null>;
    currentCategoryId: GetterWithAllArguments<number | null>;
    currentTournamentId: GetterWithAllArguments<number | null>;
}

type Getters = GetterTree<CompetitionsState, IState> & StoreGetters;

const tournamentIdCache: Record<string, number> = {};
const categoryIdCache: Record<string, number> = {};
const sportIdCache: Record<string, number> = {};

const getters: Getters = {
    availableTabs(state, getters, rootState, rootGetters) {
        const virtualTournament = rootGetters['data/competitions/currentVirtualTournament'] as VirtualTournament;
        const areMicroFeedsEnabled = rootGetters['social/areMicroFeedsEnabled'];
        const isMicroFeedsNewLabelEnabled = rootGetters['social/isMicroFeedsNewLabelEnabled'];

        const tabs: CompetitionTab[] = [];

        if (areMicroFeedsEnabled) {
            tabs.push({
                id: AvailableCompetitionTabs.Social,
                name: 'social.social_section.title',
                showBadge: isMicroFeedsNewLabelEnabled,
            });
        }

        tabs.push({
            id: AvailableCompetitionTabs.Matches,
            name: 'competition.matchesTab.name',
        });

        if (rootGetters['data/competitions/outrightOffer']) {
            tabs.push({
                id: AvailableCompetitionTabs.Outrights,
                name: 'competition.outrightsTab.name',
            });
        }

        if (virtualTournament?.features?.includes(FeatureType.FEATURETYPE_TABLE)) {
            tabs.push({
                id: AvailableCompetitionTabs.Standings,
                name: 'competition_details_tab_table',
            });
        }

        tabs.push({
            id: AvailableCompetitionTabs.Results,
            name: 'competition.resultsTab.name',
        });

        if (!areMicroFeedsEnabled && rootGetters['data/news/areSocialAnalysesEnabled']) {
            tabs.push({
                id: AvailableCompetitionTabs.Insights,
                name: 'competition_details_tab_news',
            });
        }

        return tabs;
    },
    currentSportId(_state, getters, rootState, rootGetters) {
        if (!getters.isOnCompetitionPage) {
            return null;
        }

        const { sportSlug } = getCompetitionRouteParams(rootGetters);

        const key = getSportKey(sportSlug);
        if (sportIdCache[key]) {
            return sportIdCache[key];
        }

        fillSlugToIdCaches(rootState);

        return sportIdCache[key] ?? null;
    },
    currentCategoryId(_state, getters, rootState, rootGetters) {
        if (!getters.isOnCompetitionPage) {
            return null;
        }

        const { sportSlug, categorySlug } = getCompetitionRouteParams(rootGetters);

        const key = getCategoryKey(sportSlug, categorySlug);
        if (categoryIdCache[key]) {
            return categoryIdCache[key];
        }

        fillSlugToIdCaches(rootState);

        return categoryIdCache[key] ?? null;
    },
    currentTournamentId(state, getters, rootState, rootGetters) {
        if (!getters.isOnCompetitionPage) {
            return null;
        }

        const { sportSlug, categorySlug, tournamentSlug } = getCompetitionRouteParams(rootGetters);

        const key = getTournamentKey(sportSlug, categorySlug, tournamentSlug);
        if (tournamentIdCache[key]) {
            return tournamentIdCache[key];
        }

        fillSlugToIdCaches(rootState);

        return tournamentIdCache[key] ?? null;
    },
    currentSeasonId(state, getters, rootState, rootGetters) {
        if (!getters.isOnCompetitionPage) {
            return null;
        }

        const currentVirtualTournament = rootGetters['data/competitions/currentVirtualTournament'];

        return currentVirtualTournament?.currentSeasonId ?? null;
    },

    competitionId(state, getters, rootState, rootGetters) {
        if (!getters.isOnCompetitionPage) {
            return null;
        }

        const currentVirtualTournament = rootGetters['data/competitions/currentVirtualTournament'];

        return currentVirtualTournament?.competitionId ?? null;
    },

    isOnCompetitionPage(state, getters, rootState, rootGetters) {
        const { name } = rootGetters['navigation/route'];
        return name === RouteName.Sport.Competition;
    },
};

const store = {
    namespaced: true,

    state: defaultState(),

    getters,

    mutations: {
        [mutations.SET_SELECTED_TAB]: StoreUtil.createSimpleMutator('selectedTab'),
    },

    actions: {
        setSelectedTab(
            { state, commit, dispatch, rootGetters }: ActionContext<CompetitionsState, IState>,
            tab: AvailableCompetitionTabs,
        ): boolean {
            if (tab === state.selectedTab) {
                return false;
            }
            commit(mutations.SET_SELECTED_TAB, tab);

            const query = getQueryParamsAfterTabChange(rootGetters['navigation/route'].query, tab);
            dispatch(
                'navigation/replace',
                {
                    query,
                    path: rootGetters['navigation/route'].path,
                },
                { root: true },
            );
            return true;
        },
    },
};

export type CompetitionsUIStore = typeof store;
export default store;

function getTournamentKey(sportSlug: string, categorySlug: string, tournamentSlug: string): string {
    return [sportSlug, categorySlug, tournamentSlug].join('-');
}

function getCategoryKey(sportSlug: string, categorySlug: string): string {
    return [sportSlug, categorySlug].join('-');
}

// Function defined only for consistent usage across use cases and readability
function getSportKey(sportSlug: string): string {
    return sportSlug;
}

function fillSlugToIdCaches(rootState: IState) {
    const sports = rootState.data.sportOffer.sports;
    const tournaments = rootState.data.sportOffer.tournaments;
    const categories = rootState.data.sportOffer.categories;

    const events = rootState.data.sportOffer.events;

    for (const [sportId, sport] of Object.entries(sports)) {
        const candidateSportSlug = slugify(sport.urlName);
        const sportKey = getSportKey(candidateSportSlug);
        sportIdCache[sportKey] = Number(sportId);
    }

    for (let event of Object.values(events)) {
        const categoryName = categories[event.categoryId].urlName;
        const originalTournamentName = tournaments[event.tournamentId].urlName;
        const tournamentName = simplifyTournamentName(
            originalTournamentName,
            categoryName,
        );
        const candidateSportSlug = slugify(sports[event.sportId].urlName);
        const candidateCategorySlug = slugify(categoryName);
        const candidateTournamentSlug = slugify(tournamentName);

        const categoryKey = getCategoryKey(candidateSportSlug, candidateCategorySlug);
        const tournamentKey = getTournamentKey(candidateSportSlug, candidateCategorySlug, candidateTournamentSlug);

        categoryIdCache[categoryKey] = event.categoryId;
        tournamentIdCache[tournamentKey] = event.tournamentId;
    }
}

type CompetitionTab = {
    id: AvailableCompetitionTabs;
    name: string;
    showBadge?: boolean;
};
